import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { Boxes, ChevronDown, Plus } from 'lucide-react';
import { toast } from 'sonner';

import { ApiError } from '@/shared/api-client/workspace';
import { cn } from '@/shared/lib/utils';
import { Button } from '@/shared/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/components/dropdown-menu';
import { Forbidden } from '@/modules/auth/Forbidden';
import { useAuthorization } from '@/modules/auth/useAuthorization';
import { useVenueContext } from '@/modules/venue/VenueContext';
import { useAuth } from '@/modules/auth/AuthContext';
import { MvpDefinitionsTable } from '@/modules/multivisitpasses/MvpDefinitionsTable';
import { usePasses, useSetPassActive, PASS_TYPES, type Pass, type PassType } from './passes';

/** `MULTI_VISIT` selects the dedicated Multi-Visit Pass definitions view (ADR 0064). */
type Filter = PassType | 'ALL' | 'MULTI_VISIT';
const FILTERS: Filter[] = ['ALL', ...PASS_TYPES, 'MULTI_VISIT'];
const MULTI_VISIT_TAB = 'multi-visit-passes';

export function ProductsPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const auth = useAuthorization();
  const { venues } = useAuth();
  const { venueId } = useVenueContext();

  const scope = { kind: 'venue', venueId } as const;
  const canManage = auth.can('products.pass.manage', scope);
  const canView = auth.canAny(['products.pass.view', 'products.pass.manage'], scope);

  const venue = venues.find((candidate) => candidate.id === venueId);
  const currency = venue?.currency ?? '';

  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab');
  const filter: Filter =
    tab === MULTI_VISIT_TAB
      ? 'MULTI_VISIT'
      : (PASS_TYPES.find((type) => type === tab?.toUpperCase()) ?? 'ALL');
  const multiVisit = filter === 'MULTI_VISIT';
  const passes = usePasses(
    venueId,
    filter === 'ALL' || multiVisit ? undefined : filter,
    canView && !multiVisit,
  );

  function selectFilter(value: Filter) {
    if (value === 'ALL') setSearchParams({});
    else setSearchParams({ tab: value === 'MULTI_VISIT' ? MULTI_VISIT_TAB : value.toLowerCase() });
  }

  if (!canView) return <Forbidden />;

  const items = passes.data?.items ?? [];

  return (
    <section className="grid gap-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <Boxes className="size-6" aria-hidden="true" />
            {t('products.title')}
          </h1>
          <p className="text-muted-foreground">{t('products.description', { currency })}</p>
        </div>
        {canManage && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button>
                <Plus className="size-4" aria-hidden="true" />
                {t('products.actions.create')}
                <ChevronDown className="size-4" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {PASS_TYPES.map((type) => (
                <DropdownMenuItem
                  key={type}
                  onSelect={() => navigate(`/loc/${venueId}/products/new/${type}`)}
                >
                  {t(`products.types.${type}`)}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => navigate(`/loc/${venueId}/products/multi-visit-passes/new`)}
              >
                {t('multiVisitPasses.createItem')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <div role="tablist" aria-label={t('products.title')} className="flex flex-wrap gap-1">
        {FILTERS.map((value) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={filter === value}
            onClick={() => selectFilter(value)}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:bg-accent hover:text-foreground',
              filter === value && 'bg-accent font-medium text-foreground',
            )}
          >
            {value === 'ALL' ? t('products.filters.all') : t(`products.filters.${value}`)}
          </button>
        ))}
      </div>

      {multiVisit ? (
        <MvpDefinitionsTable canManage={canManage} />
      ) : passes.isLoading ? (
        <p className="text-muted-foreground">{t('products.loading')}</p>
      ) : (
        <>
          {filter === 'ALL' && (
            <h2 className="text-lg font-semibold">{t('products.sections.passes')}</h2>
          )}
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/50 text-left">
                <tr>
                  <th className="px-4 py-2 font-medium">{t('products.columns.name')}</th>
                  <th className="px-4 py-2 font-medium">{t('products.columns.type')}</th>
                  <th className="px-4 py-2 font-medium">{t('products.columns.code')}</th>
                  <th className="px-4 py-2 font-medium">{t('products.columns.variations')}</th>
                  <th className="px-4 py-2 font-medium">{t('products.columns.status')}</th>
                  {canManage && (
                    <th className="px-4 py-2 text-right font-medium">
                      {t('products.columns.actions')}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr>
                    <td
                      colSpan={canManage ? 6 : 5}
                      className="px-4 py-6 text-center text-muted-foreground"
                    >
                      {t('products.empty')}
                    </td>
                  </tr>
                )}
                {items.map((pass) => (
                  <PassRow key={pass.id} pass={pass} canManage={canManage} />
                ))}
              </tbody>
            </table>
          </div>
          {filter === 'ALL' && (
            <>
              <h2 className="text-lg font-semibold">{t('products.filters.MULTI_VISIT')}</h2>
              <MvpDefinitionsTable canManage={canManage} />
            </>
          )}
        </>
      )}
    </section>
  );
}

function PassRow({ pass, canManage }: { pass: Pass; canManage: boolean }) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { venueId } = useVenueContext();
  const setActive = useSetPassActive(venueId);

  const variations = pass.variations
    .map((variation) => `${variation.name} ${variation.amount} ${variation.currency}`)
    .join(', ');

  async function toggleActive() {
    try {
      await setActive.mutateAsync({ passId: pass.id, active: !pass.active });
      toast.success(t(pass.active ? 'products.toast.deactivated' : 'products.toast.reactivated'));
    } catch (caught) {
      const code = caught instanceof ApiError ? caught.code : undefined;
      const key = code ? `products.errors.${code}` : 'products.errors.generic';
      const translated = t(key);
      toast.error(translated === key ? t('products.errors.generic') : translated);
    }
  }

  return (
    <tr className="border-b last:border-0">
      <td className="px-4 py-2 font-medium">{pass.name}</td>
      <td className="px-4 py-2">{t(`products.types.${pass.type}`)}</td>
      <td className="px-4 py-2 font-mono text-xs">{pass.code}</td>
      <td className="px-4 py-2 text-muted-foreground">{variations}</td>
      <td className="px-4 py-2">
        {pass.active ? t('products.status.active') : t('products.status.inactive')}
      </td>
      {canManage && (
        <td className="px-4 py-2">
          <div className="flex items-center justify-end gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => navigate(`/loc/${venueId}/products/${pass.id}/edit`)}
            >
              {t('products.actions.edit')}
            </Button>
            <Button size="sm" variant="ghost" disabled={setActive.isPending} onClick={toggleActive}>
              {pass.active ? t('products.actions.deactivate') : t('products.actions.reactivate')}
            </Button>
          </div>
        </td>
      )}
    </tr>
  );
}
